import React from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, IconButton } from '@ui/button';
import { Heading, Input, PillText } from '@web/atoms';
import { ResponsiveImage } from '@web/molecules';
import { useShopifyCart } from '@web/shopify';
import { useExperiment, HIDE_NAME_IN_EMAIL_STEP } from '@web/experiments';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { FormFieldsContainer, FormWrapper } from '../FormFieldsContainer';
import { Question } from '../Question';
import { StyledFormWithIcon } from '../StyledForm';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { onboardingSubmitted, onboardingBackArrowClicked } from '../events';
import { emailOnlySchema, schema } from './schema';
import {
  Subheadline,
  ButtonContainer,
  ArrowButtonContainer,
  ImageContainer,
  PillContainer,
} from './styles';

export const UserInfoForm = ({
  defaultValues,
  headline,
  subheadline,
  emailLabel,
  eyebrowText,
  eyebrowVariant = 'chia',
  firstNameLabel,
  firstNameQuestionPrompt,
  index,
  onSubmit,
  petName,
  ctaButtonText,
  goBack,
  image,
  flowType,
}) => {
  const reporter = useReporter();

  const { value: hideNameInput } = useExperiment(
    HIDE_NAME_IN_EMAIL_STEP,
    false,
  );

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
    clearErrors,
    control,
    reset,
    getValues,
  } = useForm({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(hideNameInput ? emailOnlySchema : schema),
  });

  const { cart } = useShopifyCart();
  const { formattedAmount } = useShopifyOfferContext();

  const goBackWithReporter = () => {
    reporter.tag(onboardingBackArrowClicked('user_info', cart?.customer));
    goBack();
  };

  const submitForm = async data => {
    reporter.tag(
      onboardingSubmitted(index, 'user_info', cart?.customer, data, flowType),
    );
    const result = await onSubmit(data);
    ApiResult.match(result, {
      error: {
        invalidEmail: () =>
          setError(
            'email',
            {
              type: 'server',
              message: 'Email is invalid',
            },
            { shouldFocus: true },
          ),
      },
    });
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
          keepErrors: true,
        }),
      ),
    );
  };

  return (
    <StyledFormWithIcon onSubmit={handleSubmit(submitForm)}>
      <FormWrapper $maxHeight={90}>
        <ArrowButtonContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'background.lightest',
            }}
            onClick={goBackWithReporter}
            disabled={isSubmitting}
            type="button"
            variant="dark"
          />
        </ArrowButtonContainer>
        <FormFieldsContainer $maxWidth={391}>
          {eyebrowText && (
            <PillContainer>
              <PillText
                text={interpolate(eyebrowText, {
                  offer: formattedAmount ?? '50%',
                })}
                variant={eyebrowVariant}
                size="xLarge"
              />
            </PillContainer>
          )}
          <ImageContainer>
            <ResponsiveImage priority={true} {...image} />
          </ImageContainer>
          {headline && (
            <Heading
              typography="heading4"
              position="center"
              headingLevel="h2"
              headingText={headline}
              verticalPadding={1}
            />
          )}
          {subheadline && <Subheadline>{subheadline}</Subheadline>}
          {!hideNameInput && (
            <Question $padding={[0, 2, 5, 2]} isUIVariant={false}>
              <Controller
                control={control}
                name="firstName"
                render={({ field, fieldState: { error } }) => (
                  <Input
                    {...field}
                    error={error?.message}
                    labelText={firstNameLabel}
                    status={error?.message ? 'error' : null}
                    visuallyHideLabel
                    centeredAlign
                  />
                )}
              />
            </Question>
          )}
          <Question $padding={[0, 2, 5, 2]} isUIVariant={false} gap={0}>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState: { error } }) => (
                <Input
                  {...field}
                  onChange={value => {
                    if (errors.email?.type === 'server') {
                      clearErrors('email');
                    }
                    field.onChange(value.trim());
                  }}
                  labelText={emailLabel}
                  error={error?.message}
                  status={error?.message ? 'error' : null}
                  visuallyHideLabel
                  centeredAlign
                />
              )}
            />
          </Question>
          <ButtonContainer>
            <Button
              type="submit"
              aria-label={ctaButtonText}
              disabled={isSubmitting}
              variant="sea2"
              isLoading={isSubmitting}
              horizontalPadding={20}
            >
              {ctaButtonText || 'Continue'}
            </Button>
          </ButtonContainer>
        </FormFieldsContainer>
      </FormWrapper>
    </StyledFormWithIcon>
  );
};

UserInfoForm.displayName = 'UserInfoForm';

UserInfoForm.propTypes = {
  defaultValues: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  emailLabel: PropTypes.string.isRequired,
  eyebrowText: PropTypes.string,
  eyebrowVariant: PropTypes.string,
  headline: PropTypes.string.isRequired,
  emailQuestionCaption: PropTypes.string.isRequired,
  emailQuestionPrompt: PropTypes.string.isRequired,
  firstNameLabel: PropTypes.string.isRequired,
  firstNameQuestionPrompt: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
  bannerMessage: PropTypes.string,
  ctaButtonText: PropTypes.object,
  flowType: PropTypes.string,
};
